import { always, any, evolve, head, identity, map, pipe, pluck, slice } from 'ramda';
import React, { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAsync, useAsyncFn } from 'react-use';
import { MainSource } from '../../constants/Source';
import { addedItemsToBoardNotification } from '../../containers/FilesManager/notifications';
import { removedFromBoardNotification } from '../../containers/FilesManager/notifications/removedFromBoardNotification';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import { MediaManagerSDKAnyItem, MediaManagerSDKBoard } from '../media-manager-sdk';
import { isBoard } from '../media-manager-sdk/boards';
import { getTranslatedItemName, isFolder } from '../media-manager-sdk/items';
import { isBoards } from '../media-manager-sdk/sources';
import { ActionClickOrigin } from './bi/useTrackActions/useTrackActions.types';
import { TrackAddToBoardFinishedTarget, useTrackBoardAnalytics } from './bi/useTrackBoardAnalytics';
import { useAdapter } from './useAdapter';
import { useNotificatorConsumer } from './useNotificator';
import { useSources } from './useSources';

interface IOuter {
  source: MainSource;
}

interface IBoardItemsListResponse {
  data: MediaManagerSDKBoard[];
  paging: { cursor: string; size: number };
}

export interface BoardItemsActionsBIParams {
  clickOrigin: ActionClickOrigin;
}

interface IBoardsContextType {
  boards: MediaManagerSDKBoard[];
  boardsOfItem: MediaManagerSDKBoard[];
  isLoading: boolean;
  updateBoards: () => Promise<IBoardItemsListResponse>;
  setItemsToAdd: (itemsToAdd: MediaManagerSDKAnyItem[]) => void;
  addItems: (
    boards: MediaManagerSDKBoard[],
    items: MediaManagerSDKAnyItem[],
    biParams: BoardItemsActionsBIParams,
  ) => Promise<void>;
  deleteItems: (
    board: MediaManagerSDKBoard,
    items: MediaManagerSDKAnyItem[],
    biParams: BoardItemsActionsBIParams,
  ) => Promise<void>;
}

const updateBoardsDefaultData = { data: [], paging: { cursor: null, size: null } };
const boardsOfItemResponseDefaultData = { data: [] };
const emptyBoards = [];
const emptyBoardsOfItem = [];

export const BoardsContext = createContext<IBoardsContextType>({
  boards: [],
  boardsOfItem: [],
  isLoading: false,
  updateBoards: always(Promise.resolve(updateBoardsDefaultData)),
  setItemsToAdd: (_itemsToAdd) => {},
  addItems: (_boards, _items, _biParams) => Promise.resolve(),
  deleteItems: (_board, _items, _biParams) => Promise.resolve(),
});

export const BoardsProvider: FC<IOuter> = ({ children, source }) => {
  const { isTrash } = useSources({ source });
  const { notify } = useNotificatorConsumer();

  const { mmgrSettings } = useMMGRSettings();
  const { isOnlyFavsBoardExperimentEnabled } = mmgrSettings;

  const boardsAdapter = useAdapter(MainSource.Boards);
  const { trackAddToBoardFinished } = useTrackBoardAnalytics();

  const [itemsToAdd, setItemsToAdd] = useState<MediaManagerSDKAnyItem[]>(null);
  const isBoardsRootFolder = itemsToAdd && isFolder(head(itemsToAdd)) && isBoards(head(itemsToAdd).source);
  const shouldUpdateBoards = itemsToAdd && !any(isBoard, itemsToAdd) && !isBoardsRootFolder;

  const { formatMessage } = useIntl();

  const [{ loading: isBoardsListLoading, value: boardsListResponse = updateBoardsDefaultData }, updateBoards] =
    useAsyncFn<IBoardItemsListResponse>(
      async () => {
        const response = await boardsAdapter.items.list();

        return evolve(
          {
            data: pipe(
              isOnlyFavsBoardExperimentEnabled ? slice(0, 1) : identity,
              map<MediaManagerSDKBoard, MediaManagerSDKBoard>((item) => ({
                ...item,
                name: getTranslatedItemName(item, formatMessage),
              })),
            ),
          },
          response,
        );
      },
      [boardsAdapter, isOnlyFavsBoardExperimentEnabled],
      {
        loading: false,
        value: updateBoardsDefaultData,
      },
    );

  const addItems = useCallback(
    async (boards: MediaManagerSDKBoard[], items, { clickOrigin }) => {
      await boardsAdapter.items.addItems(pluck('id', boards), items);

      notify(addedItemsToBoardNotification({ boards, items }));

      trackAddToBoardFinished(boards, {
        item: items,
        target: TrackAddToBoardFinishedTarget.Add,
        clickOrigin,
      });
    },
    [boardsAdapter, notify, trackAddToBoardFinished],
  );

  const deleteItems = useCallback(
    async (board: MediaManagerSDKBoard, items, { clickOrigin }) => {
      await boardsAdapter.items.deleteItems(board.id, pluck('id', items));

      notify(removedFromBoardNotification({ board, items }));

      trackAddToBoardFinished([board], {
        item: items,
        target: TrackAddToBoardFinishedTarget.Remove,
        clickOrigin,
      });
    },
    [boardsAdapter, notify, trackAddToBoardFinished],
  );

  useEffect(() => {
    updateBoards();
  }, [updateBoards]);

  const { loading: areBoardsOfItemLoading, value: boardsOfItemResponse = boardsOfItemResponseDefaultData } =
    useAsync(async () => {
      if (isTrash) {
        return boardsOfItemResponseDefaultData;
      }

      if (!itemsToAdd) {
        return boardsOfItemResponseDefaultData;
      }

      if (itemsToAdd.length !== 1) {
        return boardsOfItemResponseDefaultData;
      }

      if (!shouldUpdateBoards) {
        return boardsOfItemResponseDefaultData;
      }

      return boardsAdapter.source.getBoards(itemsToAdd[0].id);
    }, [isTrash, boardsAdapter, itemsToAdd, shouldUpdateBoards]);

  return (
    <BoardsContext.Provider
      value={{
        boards: isBoardsListLoading ? emptyBoards : boardsListResponse.data,
        isLoading: any(Boolean, [isBoardsListLoading, areBoardsOfItemLoading]),
        boardsOfItem: areBoardsOfItemLoading ? emptyBoardsOfItem : boardsOfItemResponse.data,
        addItems,
        deleteItems,
        updateBoards,
        setItemsToAdd,
      }}
    >
      {children}
    </BoardsContext.Provider>
  );
};

export const useBoards = () => useContext(BoardsContext);
